import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShoppingCart,
  faCreditCard,
  faChartBar,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";

import AdminNavBar from "../components/AdminNavBar";
import { useAdminStudents } from "../store/admin";
import {
  AdminPageBody,
  AdminSectionHeader,
  AdminTablePanel,
  AdminLoadingState,
} from "../components/AdminPageShell";
import ProgressBarChart from "./components/users/ProgressBarChart";
import {
  formatDate,
  toMoney,
  rowLabel,
  hoverActionStyles,
} from "./components/users/userUtils";

const tabButtonStyle = (active) => ({
  padding: "8px 16px",
  borderRadius: "8px",
  cursor: "pointer",
  fontWeight: 500,
  fontSize: "13px",
  border: "1px solid #e2e8f0",
  background: active ? "#16213A" : "#f1f5f9",
  color: active ? "#fff" : "#475569",
  transition: "all 0.15s",
});

// ===== STUDENT COURSES PAGE (purchases + subscriptions + learning progress chart) =====
const StudentCoursesPage = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();

  const {
    studentDetail,
    fetchStudent,
    studentPurchases,
    studentPurchasesLoading,
    studentPurchasesError,
    studentSubscriptions,
    studentSubscriptionsLoading,
    studentSubscriptionsError,
    studentProgress,
    studentProgressLoading,
    studentProgressError,
    fetchStudentPurchases,
    fetchStudentSubscriptions,
    fetchStudentLearningProgress,
  } = useAdminStudents();

  const [activeTab, setActiveTab] = useState("purchases");
  const [student, setStudent] = useState(null);

  // GET /api/admin/students/:id — the list endpoint is paginated, so fetch the
  // single learner directly instead of searching a capped list.
  useEffect(() => {
    if (studentId) fetchStudent(studentId);
  }, [studentId, fetchStudent]);

  useEffect(() => {
    if (studentDetail) setStudent(studentDetail);
  }, [studentDetail]);

  // Fetch purchases, subscriptions and learning progress for this student.
  useEffect(() => {
    if (studentId) {
      fetchStudentPurchases(studentId);
      fetchStudentSubscriptions(studentId);
      fetchStudentLearningProgress(studentId);
    }
  }, [studentId, fetchStudentPurchases, fetchStudentSubscriptions, fetchStudentLearningProgress]);

  const goBack = () => navigate("/admin/user-management");

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
      <AdminNavBar
        title="Student Courses"
        subTitle={student?.name || "Enrollment"}
        context="View purchases, subscriptions and learning progress for this student."
      />
      <AdminPageBody>
        <style>{hoverActionStyles}</style>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <button
              onClick={goBack}
              className="mb-2 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Student Management
            </button>
            <h4 className="mt-2 text-lg font-bold text-slate-900">
              Enrollment — {student?.name || "Student"}
            </h4>
            <span className="text-[13px] text-slate-500">
              {student?.email || ""}
            </span>
          </div>
        </div>

        {/* TABS */}
        <AdminSectionHeader
          title="Enrollment Details"
          description="Purchases, subscriptions and learning progress."
        />

        <div className="mt-3 flex gap-2 flex-wrap">
          <button style={tabButtonStyle(activeTab === "purchases")} onClick={() => setActiveTab("purchases")}>
            <FontAwesomeIcon icon={faShoppingCart} style={{ marginRight: 6 }} /> Purchases
          </button>
          <button style={tabButtonStyle(activeTab === "subscriptions")} onClick={() => setActiveTab("subscriptions")}>
            <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: 6 }} /> Subscriptions
          </button>
          <button style={tabButtonStyle(activeTab === "progress")} onClick={() => setActiveTab("progress")}>
            <FontAwesomeIcon icon={faChartBar} style={{ marginRight: 6 }} /> Learning Progress
          </button>
        </div>

        <AdminTablePanel>
          {activeTab === "purchases" && (
            <div className="p-4">
              {studentPurchasesLoading && (
                <AdminLoadingState />
              )}
              {studentPurchasesError && (
                <div style={{ color: "#dc2626", padding: "12px 0", fontSize: "13px" }}>
                  {studentPurchasesError}
                </div>
              )}
              {!studentPurchasesLoading && !studentPurchasesError && studentPurchases.length === 0 && (
                <div style={{ color: "#94a3b8", padding: "12px 0" }}>
                  <FontAwesomeIcon icon={faShoppingCart} style={{ marginRight: 8 }} />
                  No purchases recorded for this student.
                </div>
              )}
              {studentPurchases.length > 0 && (
                <div className="overflow-x-auto">
                <table className="admin-table-responsive w-full border-collapse text-[13px]">
                  <thead>
                    <tr>
                      <th className="border-b border-slate-200 px-3 py-2.5 text-left text-slate-600">Course</th>
                      <th className="border-b border-slate-200 px-3 py-2.5 text-left text-slate-600">Amount</th>
                      <th className="border-b border-slate-200 px-3 py-2.5 text-left text-slate-600">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentPurchases.map((p, i) => (
                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                        <td data-label="Course" className="px-3 py-2.5">{rowLabel(p)}</td>
                        <td data-label="Amount" className="px-3 py-2.5">{toMoney(p.amount ?? p.price ?? p.total)}</td>
                        <td data-label="Date" className="px-3 py-2.5">{formatDate(p.purchasedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          )}

          {activeTab === "subscriptions" && (
            <div className="p-4">
              {studentSubscriptionsLoading && (
                <AdminLoadingState />
              )}
              {studentSubscriptionsError && (
                <div style={{ color: "#dc2626", padding: "12px 0", fontSize: "13px" }}>
                  {studentSubscriptionsError}
                </div>
              )}
              {!studentSubscriptionsLoading && !studentSubscriptionsError && studentSubscriptions.length === 0 && (
                <div style={{ color: "#94a3b8", padding: "12px 0" }}>
                  <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: 8 }} /> No active subscriptions for this student.
                </div>
              )}
              {studentSubscriptions.length > 0 && (
                <div className="overflow-x-auto">
                <table className="admin-table-responsive w-full border-collapse text-[13px]">
                  <thead>
                    <tr>
                      <th className="border-b border-slate-200 px-3 py-2.5 text-left text-slate-600">Plan / Course</th>
                      <th className="border-b border-slate-200 px-3 py-2.5 text-left text-slate-600">Status</th>
                      <th className="border-b border-slate-200 px-3 py-2.5 text-left text-slate-600">Start / Expiry</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentSubscriptions.map((s, i) => (
                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                        <td data-label="Plan / Course" className="px-3 py-2.5">{rowLabel(s)}</td>
                        <td data-label="Status" className="px-3 py-2.5">
                          <span className="progress-pill" style={{
                            background: /active|renewed|paid/i.test(String(s.status || "")) ? "#dcfce7" : "#fee2e2",
                            color: /active|renewed|paid/i.test(String(s.status || "")) ? "#16a34a" : "#dc2626"
                          }}>{s.status || "ACTIVE"}</span>
                        </td>
                        <td data-label="Renewal / Start" className="px-3 py-2.5">{formatDate(s.startDate)} → {formatDate(s.expiryDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          )}

          {activeTab === "progress" && (
            <div className="p-4">
              <ProgressBarChart
                progress={studentProgress}
                loading={studentProgressLoading}
                error={studentProgressError}
              />
            </div>
          )}
        </AdminTablePanel>
      </AdminPageBody>
    </div>
  );
};

export default StudentCoursesPage;